/**
 * fzf-style fuzzy scorer — pure, no deps.
 *
 * Scores how well `pattern` matches `target`. Returns `null` when the
 * pattern does not match, or a non-negative integer score when it does.
 * Higher scores = better match.
 *
 * Algorithm (simplified Smith-Waterman inspired, as in fzf):
 *   1. Walk `target` left-to-right; find a subsequence that contains all
 *      chars of `pattern` in order (case-insensitive).
 *   2. Award bonus points for:
 *      - Consecutive matched characters (+3 per consecutive pair)
 *      - Match at word boundary (preceded by space, hyphen, dot, slash) (+6)
 *      - Match at the start of the string (+8)
 *      - Match at start of each word (+4 per word-start char matched)
 *   3. Subtract position penalty: sum of (index of each matched char / 4)
 *      so earlier matches score higher than later ones.
 *
 * Returns:
 *   null  — pattern is not a subsequence of target
 *   score — integer ≥ 0
 *
 * Also returns the matched indices so callers can highlight the chars.
 *
 * Track 3.L — spec: design.md §13a.4, architecture.md §4.24.
 */

export interface FzfMatch {
  score: number;
  /** Indices in `target` that were matched. */
  indices: number[];
}

const WORD_BOUNDARY_CHARS = new Set([" ", "-", ".", "/", "_"]);

/**
 * Return a FzfMatch if `pattern` is a fuzzy subsequence of `target`,
 * otherwise `null`. Both are compared case-insensitively.
 */
export function fzfScore(pattern: string, target: string): FzfMatch | null {
  if (pattern.length === 0) {
    return { score: 100, indices: [] };
  }

  const p = pattern.toLowerCase();
  const t = target.toLowerCase();

  // Phase 1: greedy forward scan — find leftmost subsequence match.
  const indices: number[] = [];
  let pi = 0;
  for (let ti = 0; ti < t.length && pi < p.length; ti++) {
    if (t[ti] === p[pi]) {
      indices.push(ti);
      pi++;
    }
  }

  if (pi < p.length) {
    // pattern is not a subsequence
    return null;
  }

  // Phase 2: score the matched positions.
  let score = 0;

  for (let i = 0; i < indices.length; i++) {
    const idx = indices[i] as number;

    // Position penalty — penalise late matches.
    score -= Math.floor(idx / 4);

    // Match at start of string.
    if (idx === 0) {
      score += 8;
    }

    // Word-boundary bonus.
    const prevChar: string | undefined = idx > 0 ? target[idx - 1] : undefined;
    if (idx > 0 && prevChar !== undefined && WORD_BOUNDARY_CHARS.has(prevChar)) {
      score += 6;
    }

    // Consecutive match bonus.
    const prevIdx = indices[i - 1];
    if (i > 0 && prevIdx !== undefined && prevIdx === idx - 1) {
      score += 3;
    }

    // Start-of-word bonus (each word that starts with this match).
    if (prevChar === undefined || WORD_BOUNDARY_CHARS.has(prevChar)) {
      score += 4;
    }
  }

  // Bonus for short targets (exact or near-exact matches score higher).
  if (target.length <= pattern.length + 2) {
    score += 10;
  }

  return { score, indices };
}

/**
 * Sort `items` by fzf score descending. Items with no match are excluded.
 * Returns a new array.
 */
export function fzfSort<T>(
  pattern: string,
  items: T[],
  getLabel: (item: T) => string,
): { item: T; match: FzfMatch }[] {
  if (pattern.length === 0) {
    return items.map((item) => ({ item, match: { score: 100, indices: [] } }));
  }

  const results: { item: T; match: FzfMatch }[] = [];

  for (const item of items) {
    const label = getLabel(item);
    const match = fzfScore(pattern, label);
    if (match !== null) {
      results.push({ item, match });
    }
  }

  results.sort((a, b) => b.match.score - a.match.score);
  return results;
}
